/**
 * Bảng điều khiển – Staff án Workspace (Thư ký/Thẩm phán) hoặc Dashboard Chánh án.
 */
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '../../hooks/redux'
import ChiefJudgeDashboard from './ChiefJudgeDashboard'
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Paper,
  Link,
} from '@mui/material'
import { Search, Add, Description, Campaign, Mail, Event, ChevronRight, NavigateNext } from '@mui/icons-material'
import { mockWorkspaceTasks } from '../../mock/data'
import type { WorkspaceTask, TaskPriority, TaskActionType } from '../../types/workspaceTask'

const PRIORITY_ORDER: TaskPriority[] = ['urgent', 'high', 'medium', 'low']
const PRIORITY_LABELS: Record<TaskPriority, string> = {
  urgent: 'Khẩn cấp',
  high: 'Cần xử lý',
  medium: 'Trung bình',
  low: 'Lịch trình',
}
const PRIORITY_COLORS: Record<TaskPriority, string> = {
  urgent: '#dc2626',
  high: '#ea580c',
  medium: '#ca8a04',
  low: '#16a34a',
}
const ACTION_ICONS: Record<TaskActionType, React.ReactNode> = {
  filing: <Description />,
  decision: <Campaign />,
  notice: <Mail />,
  session: <Event />,
  other: <Description />,
}

const TIME_FILTERS = [
  { value: 'all', label: 'Tất cả' },
  { value: 'overdue', label: 'Quá hạn' },
  { value: 'today', label: 'Hôm nay' },
  { value: 'week', label: 'Tuần này' },
]

function getDaysLeft(dueAt: string): { text: string; color: string; overdue: boolean } {
  const due = new Date(dueAt)
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  due.setHours(0, 0, 0, 0)
  const diff = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
  if (diff < 0) return { text: `Đã quá hạn ${Math.abs(diff)} ngày`, color: '#dc2626', overdue: true }
  if (diff === 0) return { text: 'Hạn chót: Hôm nay', color: '#dc2626', overdue: false }
  if (diff <= 2) return { text: `Còn ${diff} ngày`, color: '#dc2626', overdue: false }
  if (diff <= 5) return { text: `Còn ${diff} ngày`, color: '#ea580c', overdue: false }
  return { text: `Còn ${diff} ngày`, color: '#16a34a', overdue: false }
}

function formatDueDate(s: string) {
  return new Date(s).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export default function WorkspacePage() {
  const { user } = useAppSelector((state) => state.auth)
  const navigate = useNavigate()
  const [timeFilter, setTimeFilter] = useState('all')
  const isChiefJudge = user?.role === 'admin' || user?.role === 'chief_judge'

  if (isChiefJudge) {
    return (
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h4" sx={{ fontSize: 24, fontWeight: 600, color: '#1A2B4A', mb: 1 }}>
          Bảng điều khiển quản trị
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Tổng quan thụ lý, giải quyết án và năng suất Thẩm phán
        </Typography>
        <ChiefJudgeDashboard />
      </Box>
    )
  }
  const [priorityFilter, setPriorityFilter] = useState('all')
  const [sortBy, setSortBy] = useState('deadline')

  const tasks = useMemo(() => {
    let list = [...mockWorkspaceTasks]
    const now = new Date()
    if (timeFilter === 'overdue') list = list.filter((t) => new Date(t.due_at) < now)
    if (timeFilter === 'today') {
      const today = new Date(now)
      today.setHours(0, 0, 0, 0)
      const tomorrow = new Date(today)
      tomorrow.setDate(tomorrow.getDate() + 1)
      list = list.filter((t) => {
        const d = new Date(t.due_at)
        return d >= today && d < tomorrow
      })
    }
    if (timeFilter === 'week') {
      const weekEnd = new Date(now)
      weekEnd.setDate(weekEnd.getDate() + 7)
      list = list.filter((t) => new Date(t.due_at) <= weekEnd)
    }
    if (priorityFilter !== 'all') list = list.filter((t) => t.priority === priorityFilter)
    if (sortBy === 'deadline') list.sort((a, b) => new Date(a.due_at).getTime() - new Date(b.due_at).getTime())
    return list
  }, [timeFilter, priorityFilter, sortBy])

  const byPriority = useMemo(() => {
    const map: Record<TaskPriority, WorkspaceTask[]> = { urgent: [], high: [], medium: [], low: [] }
    tasks.forEach((t) => map[t.priority].push(t))
    return map
  }, [tasks])

  const handleOpenTask = (task: WorkspaceTask) => {
    if (task.action_type === 'filing' || task.action_type === 'notice') navigate(`/workspace/preliminary?tab=new`)
    else navigate(`/workspace/cases/${task.case_id}`)
  }

  const overdueCount = tasks.filter((t) => new Date(t.due_at) < new Date()).length
  const todayStart = new Date()
  todayStart.setHours(0, 0, 0, 0)
  const todayEnd = new Date(todayStart)
  todayEnd.setDate(todayEnd.getDate() + 1)
  const todayCount = tasks.filter((t) => {
    const d = new Date(t.due_at)
    return d >= todayStart && d < todayEnd
  }).length

  return (
    <Box sx={{ width: '100%', display: 'flex', gap: 3, flexWrap: 'wrap' }}>
      <Box sx={{ flex: '1 1 400px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography component="nav" variant="body2" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Link component="button" variant="body2" underline="hover" color="inherit" onClick={() => navigate('/workspace')}>
          Bảng điều khiển
        </Link>
        <NavigateNext fontSize="small" sx={{ verticalAlign: 'middle', mx: 0.25 }} />
        <Typography component="span" variant="body2" color="text.primary">Công việc cần xử lý</Typography>
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1 }}>
        <Paper variant="outlined" sx={{ px: 2, py: 1.5, minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">Tổng công việc</Typography>
          <Typography variant="h6" fontWeight={700}>{tasks.length}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ px: 2, py: 1.5, minWidth: 120, borderColor: overdueCount ? 'error.main' : undefined }}>
          <Typography variant="caption" color="text.secondary">Quá hạn</Typography>
          <Typography variant="h6" fontWeight={700} color={overdueCount ? 'error.main' : 'text.primary'}>{overdueCount}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ px: 2, py: 1.5, minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">Hạn hôm nay</Typography>
          <Typography variant="h6" fontWeight={700}>{todayCount}</Typography>
        </Paper>
      </Box>
      <Box sx={{ width: '100%', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontSize: 24, fontWeight: 600, color: '#1A2B4A' }}>
            Công việc cần xử lý
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Theo vụ án và thời hạn (đơn, thông báo, phiên, quyết định)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Tìm kiếm"
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
            sx={{ width: 200 }}
          />
          <Button variant="contained" startIcon={<Add />}>
            Tạo mới
          </Button>
        </Box>
      </Box>

      <Box sx={{ width: '100%', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', px: 2 }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Thời hạn</InputLabel>
          <Select value={timeFilter} label="Thời hạn" onChange={(e) => setTimeFilter(e.target.value)}>
            {TIME_FILTERS.map((f) => (
              <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Ưu tiên</InputLabel>
          <Select value={priorityFilter} label="Ưu tiên" onChange={(e) => setPriorityFilter(e.target.value)}>
            <MenuItem value="all">Tất cả</MenuItem>
            {PRIORITY_ORDER.map((p) => (
              <MenuItem key={p} value={p}>{PRIORITY_LABELS[p]}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Loại việc</InputLabel>
          <Select value="" label="Loại việc" disabled><MenuItem value="">Tất cả</MenuItem></Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>Sắp xếp:</Typography>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <MenuItem value="deadline">Deadline</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {tasks.length === 0 ? (
        <Card sx={{ width: '100%', py: 8, textAlign: 'center' }}>
          <CardContent>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Không có công việc chờ xử lý
            </Typography>
            <Button variant="outlined" sx={{ mt: 2 }} onClick={() => navigate('/workspace/calendar')}>
              Xem lịch làm việc
            </Button>
          </CardContent>
        </Card>
      ) : (
        PRIORITY_ORDER.map((priority) => {
          const list = byPriority[priority]
          if (list.length === 0) return null
          return (
            <Box key={priority} sx={{ width: '100%', mb: 3 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  py: 1,
                  px: 0,
                  color: 'text.secondary',
                  fontWeight: 700,
                  borderBottom: 2,
                  borderColor: PRIORITY_COLORS[priority],
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: PRIORITY_COLORS[priority] }} />
                {PRIORITY_LABELS[priority].toUpperCase()} ({list.length} việc)
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1.5 }}>
                {list.map((task) => {
                  const { text: deadlineText, color: deadlineColor, overdue } = getDaysLeft(task.due_at)
                  return (
                    <Card
                      key={task.id}
                      elevation={0}
                      sx={{
                        borderRadius: 2,
                        border: 1,
                        borderColor: 'divider',
                        borderLeft: overdue ? 4 : 0,
                        borderLeftColor: 'error.main',
                        cursor: 'pointer',
                        transition: 'box-shadow 200ms',
                        '&:hover': { boxShadow: 2 },
                      }}
                      onClick={() => handleOpenTask(task)}
                      aria-label={`${task.case_code} – ${task.action_label} – ${deadlineText}`}
                    >
                      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: PRIORITY_COLORS[task.priority], flexShrink: 0 }} />
                        <Box sx={{ color: 'text.secondary' }}>{ACTION_ICONS[task.action_type]}</Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body1" fontWeight={600} sx={{ fontSize: 15 }}>
                            Vụ án {task.case_code} – {task.party_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                            Hành động: {task.action_label}
                          </Typography>
                          <Typography variant="caption" sx={{ color: deadlineColor, fontWeight: overdue ? 600 : 500 }}>
                            {overdue && '⚠ '}{deadlineText} ({formatDueDate(task.due_at)})
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          endIcon={<ChevronRight />}
                          onClick={(e) => { e.stopPropagation(); handleOpenTask(task) }}
                          sx={{ flexShrink: 0, '&:hover': { bgcolor: 'primary.main', color: 'white' } }}
                        >
                          Xử lý ngay
                        </Button>
                      </CardContent>
                    </Card>
                  )
                })}
              </Box>
            </Box>
          )
        })
      )}
      </Box>
    </Box>
  )
}
